import { memo, useMemo } from 'react'

import { isEqual } from 'lodash'

import { Edit } from '@mui/icons-material'

import { Subheader3, Chip, Stack } from '@collaborate-health/uikit'
import { FormAnswerStatus, FormControlResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { useDispatch, useSelector } from '@tabeeb/store'
import { ControlElement, IElementContentExtension, useFormContext } from '@collaborate-health/forms'

import { proceedToControlAnswerPage } from '../../actions'
import { DisplayAnswersMode } from '../../enums'
import {
  getIsFormControlOnReviewInOriginalContent,
  getCurrentFormSelectedRecord,
  getDisplayAnswersMode,
  createGetFormControlAnswerSelector,
} from '../../selectors'

import AnswerStatus from '../AnswerStatus'
import FormControlInfoButton from '../FormControlInfoButton'
import FormQuestionAnswerer from '../FormQuestionAnswerer'
import FormQuestionMenu from '../FormQuestionMenu'

import { QuestionContainer, QuestionHeaderContainer, QuestionTitleContainer } from './FormQuestion.styles'

export interface FormQuestionProps extends IElementContentExtension {}

const FormQuestion: IElementContentExtension = ({ element, edited, required, children }) => {
  const dispatch = useDispatch()

  const readOnly = useFormContext((state) => state.readOnly)

  const displayMode = useSelector(getDisplayAnswersMode)
  const isFormRecordSelected = useSelector((state) => !!getCurrentFormSelectedRecord(state))

  const controlElement = element as ControlElement<FormControlResponse>
  const control = controlElement.meta!

  const isOnReview = useSelector((state) => getIsFormControlOnReviewInOriginalContent(state, control))

  const controlAnswerSelector = useMemo(() => createGetFormControlAnswerSelector(control.Id), [control.Id])
  const answer = useSelector(controlAnswerSelector, isEqual)

  controlElement.disabled = isOnReview

  const onProceedToAnswerPage = () => {
    if (!answer || isFormRecordSelected) {
      return
    }

    dispatch(proceedToControlAnswerPage(answer))
  }

  return (
    <QuestionContainer id={`form-question-${element.id}`}>
      {!readOnly && (
        <QuestionHeaderContainer
          direction='row'
          spacing={0.5}
          data-answer-status={edited ? FormAnswerStatus.Empty : answer?.FormAnswerStatus}
        >
          <Stack alignItems='center' direction='row' spacing={0.5}>
            {isOnReview && <Chip color='default' label='Currently in review' size='small' />}
            {!isOnReview && (
              <>
                <FormQuestionMenu control={control} answer={answer} />
                {displayMode === DisplayAnswersMode.AllLatest && answer && <FormQuestionAnswerer answer={answer} />}
              </>
            )}
          </Stack>

          {edited && <Chip icon={<Edit />} color='info' size='small' label='Edited' />}

          {!edited && <AnswerStatus answer={answer} isOnReview={isOnReview} />}
        </QuestionHeaderContainer>
      )}

      <Stack p={1} spacing={1}>
        <QuestionTitleContainer data-clickable={!isFormRecordSelected && !!answer} onClick={onProceedToAnswerPage}>
          <Subheader3 mr={0.25}>{element.name}</Subheader3>
          {required && (
            <Subheader3 mr={0.25} color='error'>
              *
            </Subheader3>
          )}
          <FormControlInfoButton control={control} />
        </QuestionTitleContainer>

        {children}
      </Stack>
    </QuestionContainer>
  )
}

FormQuestion.displayName = 'FormQuestion'

export default memo(FormQuestion)

